/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useEffect } from 'react';
import { useTranslation } from 'next-i18next';
import { GetStaticProps, NextLayoutComponentType } from 'next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import MainLayout from '@/layouts/main';
import { localeList } from '@/public/locales/locale-list';
import AppContainer from '@/components/layout/container';
import { Api } from '@/api';
import { allRecordsPaginationOptions } from '@/infrastructure/const/pagination';
import s from './styles.module.scss';
import { IReadSurah } from '@/infrastructure/interfaces/i-read-surah';
import ReadSurahsList from '@/components/read-surahs/list';
import { readSurahListFields } from '@/infrastructure/class/read-surah';
import { IBreadcrumb } from '@/components/common/breadcrumbs';
import { routes } from '@/infrastructure/const/routes';
import Head from 'next/head';
import { getLocaleForUrl } from '@/infrastructure/functions/get-locale-for-url';
import { useRouter } from 'next/router';
import {
  baseMarkupSchema,
  breadcrumbListMarkupSchema,
  organizationMarkupSchema,
  webPageMarkupSchema,
  webSiteMarkupSchema,
} from '@/infrastructure/markup-schemas';
import {
  BreadcrumbContext,
  IBreadcrumbContextType,
} from '@/infrastructure/context/breadcrumb-context';
import { decryptPageProps } from '@/utils/crypto/page-props-decrypt';
import { IEncryptedProps } from '@/infrastructure/interfaces/i-encrypted-props';

const api = Api.getInstance();
const SITE_URL = process.env.NEXT_PUBLIC_SITE_URL;

interface ISurahReadProps {
  readSurahs: IReadSurah[];
  serverPage?: number;
}

const SurahRead: NextLayoutComponentType<IEncryptedProps> = (props) => {
  const { data } = props;
  const decrypted = decryptPageProps<ISurahReadProps>(data);
  const { readSurahs, serverPage } = decrypted;

  const router = useRouter();
  const { locale } = router;

  const { setBreadcrumbs } = useContext(
    BreadcrumbContext
  ) as IBreadcrumbContextType;

  const { t: tMeta } = useTranslation('meta');
  const { t: tCommon } = useTranslation('common');
  const { t: tLinks } = useTranslation('links');

  const breadcrumbs: IBreadcrumb[] = [
    {
      title: tLinks('home'),
      path: routes.home,
    },
    {
      title: tLinks('surahs'),
      path: routes.surahRead,
    },
  ];

  const canonicalURL =
    `${SITE_URL}${getLocaleForUrl(locale)}` + routes.surahRead;

  const htmlCanonical = (): string => {
    return `${canonicalURL}${
      Number(serverPage) > 1 ? `?page=${Number(serverPage)}` : ''
    }`;
  };
  const htmlTitle = (): string => {
    return `${tMeta('surahsPageTitle')} ${
      Number(serverPage) > 1 ? `- ${tCommon('page')} ${Number(serverPage)}` : ''
    }`;
  };
  const htmlDescription = (): string => {
    return `${tMeta('surahsPageDescription')} ${
      Number(serverPage) > 1 ? `- ${tCommon('page')} ${Number(serverPage)}` : ''
    }`;
  };

  const shema = baseMarkupSchema([
    webPageMarkupSchema(routes.surahRead, htmlTitle(), htmlDescription()),
    webSiteMarkupSchema(),
    organizationMarkupSchema(),
    breadcrumbListMarkupSchema(breadcrumbs),
  ]);

  useEffect(() => {
    setBreadcrumbs(breadcrumbs);
  }, []);

  return (
    <>
      <Head>
        <title>{htmlTitle()}</title>
        <meta name="description" content={htmlDescription()} />
        <link rel="canonical" href={htmlCanonical()} />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(shema) }}
        />
      </Head>
      <main>
        <AppContainer>
          <ReadSurahsList
            items={readSurahs}
            serverPage={serverPage}
            className={s['read-surah']}
            mainTitle
            withPagination
          />
        </AppContainer>
      </main>
    </>
  );
};

SurahRead.getLayout = (page) => <MainLayout>{page}</MainLayout>;

export const getStaticProps: GetStaticProps = async ({ locale = '' }) => {
  try {
    const { data: readSurahsResult } = await api.getReadSurahs(
      allRecordsPaginationOptions,
      readSurahListFields
    );
    const { results: readSurahs } = readSurahsResult;

    const dataToEncrypt = {
      readSurahs,
      serverPage: 1,
    };
    const { encryptPageProps } = await import('@/utils/crypto/page-props-encrypt');
    const encrypted = encryptPageProps(dataToEncrypt);

    return {
      props: {
        data: encrypted,
        ...(await serverSideTranslations(locale, localeList)),
      },
    };
  } catch (error) {
    console.error(error);
    return {
      notFound: true,
    };
  }
};

export default SurahRead;
